import {
  FullBracket,
  LabeledGirone,
  TournamentOrganizer,
} from "../api/Tournaments";
import { TournamentFormula } from "../pages/Tournaments/GenerateSchedule";
import { Sport } from "./Sport";
import { Structure } from "./Structure";
import { UserDTO } from "./User";

export enum PairType {
  Men = "Men",
  Women = "Women",
  Mixed = "Mixed",
}

export type TournamentRoundRobin = {
  id?: string;
  tournamentId: string;
  gironi: LabeledGirone[];
};

export type FootballMember = {
  // Assente per gli ospiti (giocatori non registrati su GetMove).
  userId?: string | null;
  firstName: string;
  lastName?: string;
  avatarUrl?: string | null;
  // true = ospite non registrato (aggiunto a nome libero).
  guest?: boolean;
};

export type FootballTeam = {
  id: string;
  name: string;
  tournamentId: string;
  presidentId: string;
  members: FootballMember[];
};

// Formato d'iscrizione del torneo (deciso alla creazione).
export enum TournamentFormat {
  Standard = "standard",
  Americano = "americano",
}

export class Tournament {
  id?: string;
  name: string;
  description?: string;
  mainColor?: string;
  fitp: boolean;
  link?: string;
  maxPairs?: number;
  maxPlayersPerTeam?: number;
  date: string;
  // Data di fine opzionale: se assente coincide con `date` (inizio).
  endDate?: string;
  registrationDeadline: string;
  imageUrl?: string;
  structureId: string;
  sportId: number;
  type: PairType;
  ended?: boolean;
  organizers?: TournamentOrganizer[];
  publicBracket?: boolean;
  // Formato d'iscrizione: "standard" (coppie/squadre) o "americano" (individuale).
  format?: TournamentFormat;
  // Americano: capienza in giocatori singoli + regole di punteggio della partita.
  maxPlayers?: number;
  americanoTargetPoints?: number;
  americanoWinBy?: number;

  tournamentBracket?: TournamentBracket;

  sport?: Sport;
  structure?: Structure;

  constructor(tournament?: Tournament) {
    this.structureId = tournament?.structureId || "";
    this.name = tournament?.name || "";
    this.type = tournament?.type || PairType.Men;
    this.sportId = tournament?.sportId || 1;
    this.fitp = tournament?.fitp || false;
    this.link = tournament?.link || "";
    this.date = tournament?.date || "";
    this.registrationDeadline = tournament?.registrationDeadline || "";
    this.ended = tournament?.ended || false;
    this.format = tournament?.format || TournamentFormat.Standard;
    this.maxPlayers = tournament?.maxPlayers;
    this.americanoTargetPoints = tournament?.americanoTargetPoints;
    this.americanoWinBy = tournament?.americanoWinBy;
  }
}

// Riga di classifica di un torneo Americano (per singolo giocatore).
export type AmericanoStanding = {
  id: string;
  name: string;
  points: number;
  conceded: number;
  diff: number;
  played: number;
};

// Criterio di ordinamento della classifica dei gironi (a parità di punti).
export type StandingsCriteria = "difference" | "headToHead";

export type TournamentBracket = {
  id?: string;
  tournamentId: string;
  formula: TournamentFormula;
  gironi?: string;
  goldBracket?: FullBracket;
  silverBracket?: FullBracket;
  rounds?: FullBracket;
  roundRobin?: TournamentRoundRobin;
  tournament: Tournament;
  // "difference" (default) | "headToHead" (scontri diretti).
  standingsCriteria?: StandingsCriteria;
  // Gironi con andata e ritorno.
  roundTrip?: boolean;
  // Calcio: i pareggi nei gironi si decidono ai calci di rigore/shootout.
  shootout?: boolean;
  // Punti assegnati a chi vince/perde ai rigori.
  shootoutWinPoints?: number;
  shootoutLosePoints?: number;
  // Gironi: quante coppie/squadre per girone si qualificano al Gold (null nei
  // tabelloni vecchi: si ricava dal Gold stesso).
  coppieGold?: number | null;
  // Gironi: quante "migliori (coppieGold+1)-esime" sono ripescate nel Gold.
  bestExtra?: number | null;
  // "vsSeeds" (contro le migliori prime) | "amongThemselves" (spareggio tra loro).
  bestExtraMode?: "vsSeeds" | "amongThemselves" | null;
};

export type Pair = {
  id: string;
  player1Id?: string;
  player2Id?: string;
  // Ospiti (giocatori non registrati su GetMove): il posto è occupato dal solo
  // nome, con `playerXId` assente.
  player1Name?: string | null;
  player2Name?: string | null;
  tournamentId: string;
  type: PairType;

  player1?: UserDTO;
  player2?: UserDTO;
  tournament: Tournament;
};

export type CreatePairDTO = {
  player1Id?: string;
  player2Id?: string;
  player1Name?: string;
  player2Name?: string;
  tournamentId: string;
  type: PairType;
};
